import { useEffect } from "react";
import {
  MiBox,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiLink,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  ICONS_WEB,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { InspectionTemplateModal } from "./InspectionTemplateModal";
import { useManageInspectionTemplateViewModel } from "./useManageInspectionTemplateViewModel";

export function ManageInspectionTemplate() {
  const router = useRouter();

  const {
    inspectionTemplates,
    trades,
    isAddModalVisible,
    isDeleteModalVisible,
    isLoading,
    selectedInspectionTemplate,
    loadInspectionTemplates,
    handleAddInspectionTemplate,
    toggleAddModal,
    openDeleteModal,
    onDelete,
    closeDeleteModal,
    getTrades,
  } = useManageInspectionTemplateViewModel();

  const { hasPermission } = usePermissions();

  const theme = useTheme();

  useEffect(() => {
    loadInspectionTemplates();
    getTrades();
  }, []);

  const canUpdateInspectionTemplates = hasPermission(
    PERMISSIONS.INSPECTION_TEMPLATE.UPDATE
  );
  const canCreateInspectionTemplates = hasPermission(
    PERMISSIONS.INSPECTION_TEMPLATE.CREATE
  );
  const canDeleteInspectionTemplates = hasPermission(
    PERMISSIONS.INSPECTION_TEMPLATE.DELETE
  );

  const inspectionFieldset = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      flex: 1,
      renderCell: canUpdateInspectionTemplates
        ? ({ row: { id, index }, value }) => (
            <MiLink
              key={id}
              title={value}
              to={`/settings/order-management/inspection-types/${id}/details`}
              data-testid={`inspection-type-link-${index}`}
            />
          )
        : null,
    },
    {
      headerName: "Abbr.",
      field: "shortName",
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      headerName: "Color",
      field: "color",
      renderCell: ({ value }) => <input type="color" value={value} disabled />,
    },
  ];

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.DELETE,
      onClick: openDeleteModal(row),
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          color={theme.palette.red.main}
        />
      ),
    },
  ];
  const customOnBack = () => {
    router.navigate(`/settings/order-management`);
  };

  return (
    <>
      <MiDetailPageHeader
        title={ENTITY_NAMES.INSPECTION_TEMPLATES}
        titleIcon={mdi[ICONS_WEB.CHEVRON_LEFT]}
        loading={isLoading}
        customOnBack={customOnBack}
        actionComponent={
          canCreateInspectionTemplates && (
            <MiButton
              startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
              variant={BUTTON_VARIANTS.CONTAINED}
              onClick={toggleAddModal}
              data-testid="add-inspection-template"
              title={`Add ${ENTITY_NAMES.INSPECTION_TEMPLATE}`}
            />
          )
        }
      />
      <MiBox>
        <MiList
          columns={inspectionFieldset}
          data={inspectionTemplates}
          paginationMode={PAGINATION.CLIENT}
          loading={isLoading}
          getRowActions={canDeleteInspectionTemplates && getRowActions}
          data-testid="order-templates-table"
        />
      </MiBox>
      <InspectionTemplateModal
        open={isAddModalVisible}
        title={`Add ${ENTITY_NAMES.INSPECTION_TEMPLATE}`}
        trades={trades}
        buttonTitle={`Add ${ENTITY_NAMES.INSPECTION_TEMPLATE}`}
        onClose={toggleAddModal}
        onSubmit={handleAddInspectionTemplate}
        loading={isLoading}
      />
      <MiModalConfirm
        title="Confirm Delete"
        description={`Are you sure you want to delete: ${selectedInspectionTemplate.name}?`}
        open={isDeleteModalVisible}
        handlePositive={onDelete}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={closeDeleteModal}
      />
    </>
  );
}
